import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { UrlGeneratorProvider, createStore } from '@folklore/react-container';
import { TrackingContainer } from '@folklore/tracking';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';

import reducers from '../reducers/index';
import * as AppPropTypes from '../lib/PropTypes';
import { KeysProvider } from '../contexts/KeysContext';
import { NewsletterProvider } from '../contexts/NewsletterContext';
import { PopupProvider } from '../contexts/PopupContext';

import App from './App';

const propTypes = {
    locale: PropTypes.string,
    messages: PropTypes.oneOfType([
        PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
        PropTypes.objectOf(PropTypes.string),
    ]),
    routes: PropTypes.objectOf(PropTypes.string),
    statusCode: AppPropTypes.statusCode,
    googleApiKey: PropTypes.string,
    page: AppPropTypes.page,
    season: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), // eslint-disable-line
    menus: PropTypes.object, // eslint-disable-line
    newsletter: PropTypes.bool,
    donationUrl: PropTypes.string,
};

const defaultProps = {
    locale: 'fr',
    messages: {},
    routes: {},
    statusCode: null,
    googleApiKey: null,
    page: null,
    season: null,
    menus: null,
    newsletter: false,
    donationUrl: null,
};

const Root = ({
    locale,
    messages,
    routes,
    statusCode,
    googleApiKey,
    page,
    season,
    menus,
    newsletter,
    donationUrl,
}) => {
    const pages = page ? { [window.location.pathname || page.url]: page } : {};

    const store = useMemo(
        () =>
            createStore(reducers, {
                site: {
                    statusCode,
                    donationUrl,
                    pages,
                    menus,
                    season,
                    color: season.color || '#000000',
                    theme: page && page.theme ? page.theme : 'dark',
                },
            }),
        [statusCode, donationUrl],
    );
    const keys = useMemo(
        () => ({
            googleApiKey,
        }),
        [googleApiKey],
    );

    const { popup = null } = page || {};

    // console.log('Root props', page); // eslint-disable-line
    return (
        <ReduxProvider store={store}>
            <IntlProvider locale={locale} messages={messages[locale] || messages}>
                <BrowserRouter>
                    <UrlGeneratorProvider routes={routes}>
                        <KeysProvider keys={keys}>
                            <NewsletterProvider newsletter={newsletter}>
                                <PopupProvider popup={popup}>
                                    <TrackingContainer>
                                        <App />
                                    </TrackingContainer>
                                </PopupProvider>
                            </NewsletterProvider>
                        </KeysProvider>
                    </UrlGeneratorProvider>
                </BrowserRouter>
            </IntlProvider>
        </ReduxProvider>
    );
};

Root.propTypes = propTypes;
Root.defaultProps = defaultProps;

export default Root;
